/**
 * The date module. Every date conversion in the monorepo lives here — see
 * the "Date-handling policy" in
 * docs/superpowers/plans/2026-07-31-utc-day-boundary-remediation.md.
 *
 * Three semantic categories, each with its own rules:
 * - Instant: a moment on the global timeline. Stored as timestamptz (UTC),
 *   exchanged as ISO-8601 with `Z`.
 * - Civil date: a calendar day with no zone and no time. Exchanged as a bare
 *   'yyyy-MM-dd' string that NEVER passes through `Date`/`toISOString()`.
 * - Civil time / recurrence: wall-clock rules that re-resolve per occurrence,
 *   carried together with an IANA zone.
 *
 * Deriving "which day" from an instant always requires a zone; `toCivilDate`
 * uses the runtime's local zone (browser = the user's zone).
 */

/**
 * The zone legacy-instant normalization and migration backfills fall back
 * to, and the platform-wide default for anything not yet zone-aware.
 * Multi-zone tenancy is real: organization-scoped day/month cuts resolve
 * through `OrganizationTimezoneService` (backend), which falls back to this
 * constant, never the other way around. Foundation 1's rule: personal data
 * keeps the user's own zone, organization data uses the organization's.
 */
export const DEFAULT_PLATFORM_TIMEZONE = 'Europe/Belgrade';

const isUtcMidnight = (d: Date) =>
  d.getUTCHours() === 0 &&
  d.getUTCMinutes() === 0 &&
  d.getUTCSeconds() === 0 &&
  d.getUTCMilliseconds() === 0;

/**
 * Resolves a caller-supplied calendar range into an instant window.
 *
 * The calendar FE sends real instants (local-day bounds serialized as ISO),
 * which pass through unchanged. Legacy callers send bare `yyyy-MM-dd`
 * values — by the time zod's `z.coerce.date()` has run, those are exact UTC
 * midnights — and for them the historical behavior is preserved: the range
 * is widened to whole UTC days. A genuinely UTC-located client whose local
 * midnight coincides with UTC midnight gets the same window either way, so
 * the midnight-pair heuristic cannot misfire.
 */
export function resolveCalendarWindow(
  from: Date | string,
  to: Date | string,
): { end: Date; start: Date } {
  const start = new Date(from);
  const end = new Date(to);

  if (isUtcMidnight(start) && isUtcMidnight(end)) {
    end.setUTCHours(23, 59, 59, 999);
  }

  return { end, start };
}

/**
 * The calendar's query window as real instants: local midnight of the first
 * visible day through local end-of-day of the last. Runs in the RUNTIME's
 * local zone — meant for the FE (browser), where "local" is the user's zone.
 */
export function localDayWindow(
  first: Date,
  last: Date,
): { from: string; to: string } {
  const from = new Date(first);
  from.setHours(0, 0, 0, 0);
  const to = new Date(last);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * The LOCAL calendar day of an instant as a civil 'yyyy-MM-dd' string.
 * Never use `toISOString().slice(0, 10)` for this — it renders the UTC day
 * and shifts the date for any zone east of UTC during the first hours of
 * the day (and west of UTC during the last).
 */
export function toCivilDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear()).padStart(4, '0')}-${month}-${day}`;
}

/**
 * Civil-day arithmetic that never leaves the string domain. The UTC anchor
 * is purely an internal parse frame — strings in, strings out, no local
 * zone involved.
 */
export function addCivilDays(civil: string, days: number): string {
  const date = new Date(`${civil}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  // eslint-disable-next-line no-restricted-syntax -- UTC-anchored string math, not a local render
  return date.toISOString().slice(0, 10);
}

/** Monday of the civil day's week (ISO weeks), string domain. */
export function weekStartOf(civil: string) {
  // getUTCDay of a UTC-anchored civil day: 0 = Sunday
  const weekday = new Date(`${civil}T00:00:00Z`).getUTCDay();
  return addCivilDays(civil, -((weekday + 6) % 7));
}

/**
 * A civil day as a LOCAL-midnight Date — the inverse of `toCivilDate`, for
 * the picker/display domain only (MUI pickers and `toLocaleDateString` work
 * on local Dates). Never serialize the result with `toISOString()`.
 */
export function fromCivilDate(civil: string): Date {
  const [year, month, day] = civil.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Civil-day arithmetic in whole calendar months, string domain, clamping to
 * the end of a shorter target month (Jan 31 + 1 month = Feb 28) — the
 * subscription-billing convention, unlike JS `setMonth`, which overflows
 * into the next month.
 */
export function addCivilMonths(civil: string, months: number): string {
  const [year, month, day] = civil.split('-').map(Number);
  const targetMonthStart = new Date(Date.UTC(year, month - 1 + months, 1));
  const daysInTarget = new Date(
    Date.UTC(
      targetMonthStart.getUTCFullYear(),
      targetMonthStart.getUTCMonth() + 1,
      0,
    ),
  ).getUTCDate();
  targetMonthStart.setUTCDate(Math.min(day, daysInTarget));
  // eslint-disable-next-line no-restricted-syntax -- UTC-anchored string math, not a local render
  return targetMonthStart.toISOString().slice(0, 10);
}

/** Signed whole-day distance between two civil days, string domain. */
export function civilDaysBetween(from: string, to: string): number {
  const DAY_MS = 24 * 3600 * 1000;
  return Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS,
  );
}

export const CIVIL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Whether a string is a bare civil day ('yyyy-MM-dd'), not an instant. */
export function isCivilDate(value: unknown): value is string {
  return typeof value === 'string' && CIVIL_DATE_RE.test(value);
}

/**
 * Whether a civil-shaped string names a day that exists on the calendar —
 * '2026-02-30' is civil-shaped but must never reach a DATE column or a
 * `businessDate` filter (Postgres rejects it as a 500, not a 400).
 */
export function isRealCivilDay(value: string): boolean {
  if (!isCivilDate(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day));
  return (
    utc.getUTCFullYear() === year &&
    utc.getUTCMonth() === month - 1 &&
    utc.getUTCDate() === day
  );
}

/**
 * A valid wire from/to value for day-anchored list queries: a REAL civil
 * day, or a parseable legacy instant.
 */
export function isWireDay(value: string): boolean {
  return isCivilDate(value)
    ? isRealCivilDay(value)
    : !Number.isNaN(Date.parse(value));
}

/**
 * A wire date value for the picker/display domain: a civil day parses to
 * LOCAL midnight (`fromCivilDate`), anything else — legacy cached instants —
 * through `new Date`.
 */
export function parseWireDay(value: string): Date {
  return isCivilDate(value) ? fromCivilDate(value) : new Date(value);
}

/**
 * Normalizes a wire value into a civil day: civil strings pass through;
 * legacy instants (stale bundles serialized the picker's local midnight via
 * `toISOString()`) recover the intended day in the given zone — the same
 * rule the column-migration backfills use.
 */
export function coerceCivilDate(value: Date | string, timeZone: string) {
  if (isCivilDate(value)) return value;
  return civilDateInZone(new Date(value), timeZone);
}

/**
 * Routes a calendar range to the right filter for day-anchored scheduling:
 * a civil day pair queries the frozen `businessDate` column directly (no
 * zone anywhere), while instants — or legacy zod-coerced Dates — fall back
 * to the `dateTime` window via `resolveCalendarWindow`.
 */
export function resolveScheduleDayFilter(
  from: Date | string,
  to: Date | string,
):
  | { from: string; kind: 'civil'; to: string }
  | { end: Date; kind: 'instant'; start: Date } {
  if (isCivilDate(from) && isCivilDate(to)) {
    return { from, kind: 'civil', to };
  }
  const { end, start } = resolveCalendarWindow(from, to);
  return { end, kind: 'instant', start };
}

// ---------------------------------------------------------------------------
// IANA-zone conversions (Intl-based, no library). These are the ONLY
// sanctioned way to derive "which day / what wall time" from an instant in a
// zone other than the runtime's own — fixed offsets are never correct across
// DST.

const zonedFormatters = new Map<string, Intl.DateTimeFormat>();

function zonedFormatter(timeZone: string) {
  let formatter = zonedFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
      minute: '2-digit',
      month: '2-digit',
      second: '2-digit',
      timeZone,
      year: 'numeric',
    });
    zonedFormatters.set(timeZone, formatter);
  }
  return formatter;
}

function zonedParts(instant: Date, timeZone: string) {
  const parts: Record<string, number> = {};
  for (const { type, value } of zonedFormatter(timeZone).formatToParts(
    instant,
  )) {
    if (type !== 'literal') parts[type] = Number(value);
  }
  return parts as {
    day: number;
    hour: number;
    minute: number;
    month: number;
    second: number;
    year: number;
  };
}

/**
 * Returns `timeZone` if it is a usable IANA name, else `fallback`.
 * Constructing the formatter IS the validation (RangeError on unknown
 * zones) — the same check every zone consumer here would otherwise hit at
 * use time, e.g. a raw user-settings string reaching `Intl` or SQL
 * `AT TIME ZONE`.
 */
export function safeTimeZone(
  timeZone: null | string | undefined,
  fallback: string,
): string {
  if (!timeZone) return fallback;
  try {
    Intl.DateTimeFormat('en-US', { timeZone });
    return timeZone;
  } catch {
    return fallback;
  }
}

/** The civil calendar day of an instant in the given IANA zone. */
export function civilDateInZone(instant: Date, timeZone: string): string {
  const { day, month, year } = zonedParts(instant, timeZone);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The wall-clock time of an instant in the given IANA zone. */
export function wallTimeInZone(
  instant: Date,
  timeZone: string,
): { hour: number; minute: number } {
  const { hour, minute } = zonedParts(instant, timeZone);
  return { hour, minute };
}

/**
 * The exact instant at which a wall clock in `timeZone` shows
 * `civil` + `hour:minute`. Two-pass offset resolution: guess as if the zone
 * were UTC, correct by the offset observed at the guess, then re-check —
 * which also lands on a sane instant when the wall time falls inside a DST
 * gap or overlap: a nonexistent spring-forward time shifts forward by the
 * skipped hour, and an ambiguous fall-back time resolves to the
 * POST-transition occurrence. Both pinned in date-utils.spec.ts.
 */
export function instantAtZonedWallTime(
  civil: string,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const [year, month, day] = civil.split('-').map(Number);
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);

  const offsetAt = (ts: number) => {
    const p = zonedParts(new Date(ts), timeZone);
    return (
      Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - ts
    );
  };

  let ts = utcGuess - offsetAt(utcGuess);
  const secondOffset = offsetAt(ts);
  if (utcGuess - secondOffset !== ts) {
    ts = utcGuess - secondOffset;
  }
  return new Date(ts);
}

/**
 * The first instant of the civil `day` in `timeZone` — its midnight, or, when
 * the zone skips that midnight (DST starting at 24:00, e.g. Azores, Havana,
 * Santiago), the transition instant. `instantAtZonedWallTime` resolves a
 * skipped 00:00 BACKWARD to 23:00 of the day before, so the real start is
 * found by bisecting between that instant (previous day) and noon (`day`).
 *
 * `[startOfCivilDayInZone(d), startOfCivilDayInZone(d + 1))` is exactly the
 * set of instants whose `civilDateInZone` is `d` (23- and 25-hour days
 * included) — the half-open window an org-day filter needs. Moved here from
 * the locker-key state module so the desk sales list (BE) and its period
 * presets (FE) cut the organization's day the same way.
 */
export function startOfCivilDayInZone(day: string, timeZone: string) {
  const midnight = instantAtZonedWallTime(day, 0, 0, timeZone);
  if (civilDateInZone(midnight, timeZone) === day) return midnight;
  let before = midnight.getTime();
  let onDay = instantAtZonedWallTime(day, 12, 0, timeZone).getTime();
  while (onDay - before > 1) {
    const mid = Math.floor((before + onDay) / 2);
    if (civilDateInZone(new Date(mid), timeZone) === day) onDay = mid;
    else before = mid;
  }
  return new Date(onDay);
}
